import { Col, Dropdown, MenuProps, Row, Typography } from 'antd';
import React, { useState } from 'react';
import {
  AddButton,
  Card,
  Currency,
  DateWithoutTimeZone,
  Divider,
  Icon,
} from '@common/components';
import { InvoiceInstallmentFragmentType } from '@pay-bill/graphql';
import { RemoveInstallmentModal } from './_remove-installment-modal';
import { EditInstallmentModal } from './_edit-installment-modal';
import { CreateInstallmentModal } from './_create-installment-modal';

const { Text } = Typography;

export const InstallmentsCard = ({
  installmentsData,
}: {
  installmentsData: {
    installments: InvoiceInstallmentFragmentType[];
    remainingInstallmentBalance: number;
    canCreate: { value: boolean };
    worksInvoiceId: string;
  };
}) => {
  const [modalVisible, setModalVisible] = useState<string | undefined>();
  const [installmentModalData, setInstallmentModalData] = useState<
    InvoiceInstallmentFragmentType | undefined
  >();

  const onModalAction = () => {
    setModalVisible(undefined);
    setInstallmentModalData(undefined);
  };

  const onActionClick: MenuProps['onClick'] = ({ key }) => {
    const keyAction = key.split('_')[0];
    const id = key.split('_')[1];

    setModalVisible(keyAction);
    setInstallmentModalData(
      installmentsData.installments.find((installment) => installment.id === id)
    );
  };

  const buildActionItems = ({
    canUpdate,
    canArchive,
    id,
  }: InvoiceInstallmentFragmentType) => [
    canUpdate.value
      ? {
          label: 'Edit',
          key: `edit_${id}`,
        }
      : null,
    canArchive.value
      ? {
          label: 'Remove',
          key: `remove_${id}`,
        }
      : null,
  ];

  return (
    <>
      <Card title="Installments" className="installments-card">
        {installmentsData.installments.map((installment) => {
          const showActions =
            installment.canUpdate.value || installment.canArchive.value;

          return (
            <Row key={installment.id} style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Row>
                  <Text type="secondary">
                    Payment on <DateWithoutTimeZone date={installment.paidAt} />
                  </Text>
                </Row>
              </Col>
              <Col span={11}>
                <Row justify="end">
                  <Text>
                    <Currency value={installment.paymentAmountCents / 100} />
                  </Text>
                </Row>
              </Col>
              <Col span={1}>
                {showActions && (
                  <Dropdown
                    className="installments-dropdown"
                    menu={{
                      items: buildActionItems(installment),
                      onClick: onActionClick,
                    }}
                  >
                    <a>
                      <Icon size="lg" icon="ellipsis" kind="regular" />
                    </a>
                  </Dropdown>
                )}
              </Col>
            </Row>
          );
        })}

        <Divider gutter={12} />

        <Row>
          <Col span={12}>
            <Row>
              <Text strong>Remaining Balance</Text>
            </Row>
          </Col>
          <Col span={11}>
            <Row justify="end">
              <Text strong>
                <Currency
                  value={installmentsData.remainingInstallmentBalance / 100}
                />
              </Text>
            </Row>
          </Col>
        </Row>

        {installmentsData.canCreate.value &&
          installmentsData.remainingInstallmentBalance > 0 && (
            <Row justify="end" style={{ marginTop: 16 }}>
              <AddButton onClick={() => setModalVisible('create')}>
                Installment
              </AddButton>
            </Row>
          )}
        {installmentModalData && (
          <>
            <RemoveInstallmentModal
              visible={modalVisible === 'remove'}
              onAction={onModalAction}
              installment={installmentModalData}
            />
            <EditInstallmentModal
              visible={modalVisible === 'edit'}
              onAction={onModalAction}
              installment={installmentModalData}
              remainingInstallmentBalance={
                installmentsData.remainingInstallmentBalance
              }
            />
          </>
        )}
        <CreateInstallmentModal
          visible={modalVisible === 'create'}
          onAction={onModalAction}
          worksInvoiceId={installmentsData.worksInvoiceId}
          remainingInstallmentBalance={
            installmentsData.remainingInstallmentBalance
          }
        />
      </Card>
      <style jsx>{`
        :global(.installments-dropdown) {
          margin-left: 8px;
        }
      `}</style>
    </>
  );
};
